import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { API, AuthContext } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Plus,
  FileText,
  Copy,
  Trash2,
  Edit,
  MoreVertical,
  ExternalLink,
  Code,
  Eye,
  Table2
} from "lucide-react";
import { NewSheetDialog } from "@/components/production/NewSheetDialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const FIELD_TYPES = [
  { value: "text", label: "Text" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone" },
  { value: "textarea", label: "Text Area" },
  { value: "select", label: "Dropdown" },
];

export default function FormsPage() {
  const { user } = useContext(AuthContext);
  const [forms, setForms] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showTemplateLibrary, setShowTemplateLibrary] = useState(false);
  const [showEmbedModal, setShowEmbedModal] = useState(false);
  const [selectedForm, setSelectedForm] = useState(null);
  const [showFillModal, setShowFillModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showScratchpad, setShowScratchpad] = useState(false);
  const [fillData, setFillData] = useState({});
  const [editingForm, setEditingForm] = useState(null);
  const [newForm, setNewForm] = useState({
    name: "",
    fields: [
      { name: "first_name", label: "First Name", type: "text", required: true },
      { name: "last_name", label: "Last Name", type: "text", required: true },
      { name: "email", label: "Email", type: "email", required: true },
      { name: "phone", label: "Phone", type: "phone", required: false },
    ]
  });

  useEffect(() => {
    fetchForms();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchForms = async () => {
    try {
      const [formsRes, templatesRes] = await Promise.all([
        axios.get(`${API}/forms`).catch(() => ({ data: [] })),
        axios.get(`${API}/form-templates/library`).catch(() => ({ data: [] }))
      ]);
      setForms(formsRes.data || []);
      setTemplates(templatesRes.data || []);
    } catch (error) {
      // Error handled silently
    } finally {
      setLoading(false);
    }
  };

  const handleUseTemplate = async (templateId) => {
    try {
      await axios.post(`${API}/form-templates/use/${templateId}`);
      toast.success("Template added to your forms!");
      setShowTemplateLibrary(false);
      fetchForms();
    } catch (err) {
      toast.error("Failed to use template");
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/forms`, newForm);
      setForms([...forms, response.data]);
      setShowModal(false);
      setNewForm({
        name: "",
        fields: [
          { name: "first_name", label: "First Name", type: "text", required: true },
          { name: "last_name", label: "Last Name", type: "text", required: true },
          { name: "email", label: "Email", type: "email", required: true },
          { name: "phone", label: "Phone", type: "phone", required: false },
        ]
      });
      toast.success("Form created");
    } catch (error) {
      toast.error("Failed to create form");
    }
  };

  const handleDelete = async (formId) => {
    if (!window.confirm("Are you sure you want to delete this form?")) return;
    try {
      await axios.delete(`${API}/forms/${formId}`);
      setForms(forms.filter(f => f.form_id !== formId));
      toast.success("Form deleted");
    } catch (error) {
      toast.error("Failed to delete form");
    }
  };

  const addField = () => {
    setNewForm({
      ...newForm,
      fields: [...newForm.fields, { name: "", label: "", type: "text", required: false }]
    });
  };

  const updateField = (index, updates) => {
    const updatedFields = [...newForm.fields];
    updatedFields[index] = { ...updatedFields[index], ...updates };
    setNewForm({ ...newForm, fields: updatedFields });
  };

  const removeField = (index) => {
    setNewForm({
      ...newForm,
      fields: newForm.fields.filter((_, i) => i !== index)
    });
  };

  const copyWebhookUrl = (formId) => {
    const webhookUrl = `${process.env.REACT_APP_BACKEND_URL}/api/webhook/lead/${formId}`;
    navigator.clipboard.writeText(webhookUrl);
    toast.success("Webhook URL copied to clipboard");
  };

  const showEmbed = (form) => {
    setSelectedForm(form);
    setShowEmbedModal(true);
  };

  const getEmbedCode = (form) => {
    const webhookUrl = `${process.env.REACT_APP_BACKEND_URL}/api/webhook/lead/${form.form_id}`;
    return `<!-- LeadFlow Form Embed -->
<form id="leadflow-form-${form.form_id}" onsubmit="submitLeadFlowForm(event)">
  ${form.fields.map(field => `
  <div class="form-group">
    <label for="${field.name}">${field.label}${field.required ? ' *' : ''}</label>
    ${field.type === 'textarea' 
      ? `<textarea name="${field.name}" id="${field.name}" ${field.required ? 'required' : ''}></textarea>`
      : `<input type="${field.type === 'phone' ? 'tel' : field.type}" name="${field.name}" id="${field.name}" ${field.required ? 'required' : ''} />`
    }
  </div>`).join('')}
  <button type="submit">Submit</button>
</form>

<script>
async function submitLeadFlowForm(e) {
  e.preventDefault();
  const formData = new FormData(e.target);
  const data = Object.fromEntries(formData);
  
  try {
    const response = await fetch('${webhookUrl}', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (response.ok) {
      alert('Thank you! We will be in touch soon.');
      e.target.reset();
    }
  } catch (error) {
    alert('Something went wrong. Please try again.');
  }
}
</script>`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="forms-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Lead Forms
          </h1>
          <p className="text-slate-600 mt-1">Create embeddable forms to capture leads</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowTemplateLibrary(true)} data-testid="template-library-btn">
            <FileText className="w-4 h-4 mr-2" /> Template Library
          </Button>
          <Button onClick={() => setShowModal(true)} data-testid="create-form-btn">
            <Plus className="w-4 h-4 mr-2" /> Create Form
          </Button>
        </div>
      </div>

      <section className="flex flex-wrap items-center justify-between gap-4 border-y border-slate-200 bg-slate-50 px-4 py-3" data-testid="shop-worksheets-section">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded bg-slate-900 text-amber-400"><Table2 className="h-4 w-4" /></div>
          <div><h2 className="text-base font-semibold text-slate-900">Shop Worksheets</h2><p className="text-sm text-slate-500">Open a client-linked production scratchpad with automated finishing routing.</p></div>
        </div>
        <Button variant="outline" onClick={() => setShowScratchpad(true)} data-testid="forms-new-scratchpad-button"><Plus className="mr-2 h-4 w-4" /> New Production Scratchpad</Button>
      </section>

      {/* Forms List */}
      {forms.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {forms.map((form) => (
            <Card 
              key={form.form_id} 
              className="card-hover cursor-pointer hover:border-indigo-300 transition-colors"
              onClick={() => { setSelectedForm(form); setFillData({}); setShowFillModal(true); }}
              data-testid={`form-card-${form.form_id}`}
            >
              <CardHeader className="flex flex-row items-start justify-between">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                    <FileText className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <CardTitle className="text-base">{form.name}</CardTitle>
                    <p className="text-sm text-slate-500 mt-1">
                      {form.fields?.length || 0} fields
                    </p>
                  </div>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <MoreVertical className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setSelectedForm(form); setFillData({}); setShowFillModal(true); }}>
                      <FileText className="w-4 h-4 mr-2" />
                      Open Form
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setEditingForm(JSON.parse(JSON.stringify(form))); setShowEditModal(true); }}>
                      <Edit className="w-4 h-4 mr-2" />
                      Edit Fields
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); showEmbed(form); }}>
                      <Code className="w-4 h-4 mr-2" />
                      Get Embed Code
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); copyWebhookUrl(form.form_id); }}>
                      <Copy className="w-4 h-4 mr-2" />
                      Copy Webhook URL
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={(e) => { e.stopPropagation(); handleDelete(form.form_id); }}
                      className="text-red-600"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {form.fields?.slice(0, 3).map((field, index) => (
                    <div 
                      key={index} 
                      className="flex items-center justify-between text-sm bg-slate-50 rounded px-3 py-2"
                    >
                      <span className="text-slate-700">{field.label}</span>
                      <Badge variant="outline" className="text-xs">
                        {field.type}
                      </Badge>
                    </div>
                  ))}
                  {form.fields?.length > 3 && (
                    <p className="text-xs text-slate-400 text-center">
                      +{form.fields.length - 3} more fields
                    </p>
                  )}
                </div>
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
                  <span className="text-sm text-slate-500">
                    {form.submissions_count || 0} submissions
                  </span>
                  <Badge className={form.is_active ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-600"}>
                    {form.is_active ? "Active" : "Inactive"}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="py-12">
          <CardContent>
            <div className="empty-state">
              <div className="empty-state-icon">
                <FileText className="w-8 h-8 text-slate-400" />
              </div>
              <p className="text-slate-600 font-medium">No forms yet</p>
              <p className="text-sm text-slate-400 mb-4">
                Create your first form to start capturing leads
              </p>
              <Button onClick={() => setShowModal(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Create Form
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create Form Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Lead Form</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="form-field">
              <Label>Form Name *</Label>
              <Input
                value={newForm.name}
                onChange={(e) => setNewForm({ ...newForm, name: e.target.value })}
                placeholder="e.g., Contact Form, Quote Request"
                required
                data-testid="form-name"
              />
            </div>

            <div className="form-field">
              <div className="flex items-center justify-between mb-2">
                <Label>Fields</Label>
                <Button type="button" variant="outline" size="sm" onClick={addField}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add Field
                </Button>
              </div>
              <div className="space-y-3">
                {newForm.fields.map((field, index) => (
                  <div key={index} className="border border-slate-200 rounded-lg p-3 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Field {index + 1}</span>
                      {index > 2 && (
                        <Button 
                          type="button" 
                          variant="ghost" 
                          size="sm"
                          onClick={() => removeField(index)}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <Input
                        placeholder="Field name (e.g., company)"
                        value={field.name}
                        onChange={(e) => updateField(index, { name: e.target.value })}
                      />
                      <Input
                        placeholder="Label (e.g., Company Name)"
                        value={field.label}
                        onChange={(e) => updateField(index, { label: e.target.value })}
                      />
                    </div>
                    <div className="flex items-center gap-4">
                      <Select
                        value={field.type}
                        onValueChange={(v) => updateField(index, { type: v })}
                      >
                        <SelectTrigger className="w-40">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {FIELD_TYPES.map((type) => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <label className="flex items-center gap-2 text-sm">
                        <Switch
                          checked={field.required}
                          onCheckedChange={(v) => updateField(index, { required: v })}
                        />
                        Required
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="submit-form-btn">
                Create Form
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Embed Code Modal */}
      <Dialog open={showEmbedModal} onOpenChange={setShowEmbedModal}>
        <DialogContent className="max-h-[85vh] overflow-y-auto max-w-3xl">
          <DialogHeader>
            <DialogTitle>Embed Code - {selectedForm?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="mb-2 block">Webhook URL</Label>
              <div className="flex gap-2">
                <Input
                  value={selectedForm ? `${process.env.REACT_APP_BACKEND_URL}/api/webhook/lead/${selectedForm.form_id}` : ""}
                  readOnly
                  className="font-mono text-sm"
                />
                <Button 
                  variant="outline"
                  onClick={() => selectedForm && copyWebhookUrl(selectedForm.form_id)}
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-sm text-slate-500 mt-2">
                Use this URL for Google Ads, Zapier, or other webhook integrations
              </p>
            </div>
            
            <div>
              <Label className="mb-2 block">HTML Embed Code</Label>
              <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto text-sm">
                <code>{selectedForm && getEmbedCode(selectedForm)}</code>
              </pre>
              <Button 
                variant="outline"
                className="mt-2"
                onClick={() => {
                  navigator.clipboard.writeText(getEmbedCode(selectedForm));
                  toast.success("Embed code copied");
                }}
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy Embed Code
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Template Library Modal */}
      <Dialog open={showTemplateLibrary} onOpenChange={setShowTemplateLibrary}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-indigo-600" /> Form Template Library
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500 -mt-2">Pre-made templates for kitchen & bath manufacturing. Click "Use Template" to add to your forms — then customize as needed.</p>
          <div className="space-y-3 py-2">
            {templates.map(tpl => (
              <div key={tpl.template_id} className="border rounded-lg p-4 hover:border-indigo-300 transition-colors" data-testid={`tpl-${tpl.template_id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium text-slate-900">{tpl.name}</h3>
                      <Badge variant="outline" className="text-xs capitalize">{tpl.category}</Badge>
                    </div>
                    <p className="text-sm text-slate-500 mt-1">{tpl.description}</p>
                    <p className="text-xs text-slate-400 mt-1">{tpl.fields?.length || 0} fields</p>
                  </div>
                  <Button size="sm" onClick={() => handleUseTemplate(tpl.template_id)} data-testid={`use-${tpl.template_id}`}>
                    <Plus className="w-3 h-3 mr-1" /> Use Template
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Fill Out Form Modal */}
      <Dialog open={showFillModal} onOpenChange={(v) => { setShowFillModal(v); if (!v) setFillData({}); }}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
          {selectedForm && (
            <>
              <DialogHeader>
                <DialogTitle>{selectedForm.name}</DialogTitle>
                {selectedForm.description && <p className="text-sm text-slate-500">{selectedForm.description}</p>}
              </DialogHeader>
              <div className="space-y-4 py-2">
                {(selectedForm.fields || []).map((field) => (
                  <div key={field.id || field.name}>
                    {field.type === "section_header" ? (
                      <div className="pt-4 pb-1 border-b border-slate-200 mt-2">
                        <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">{field.label}</h3>
                      </div>
                    ) : (
                    <>
                    <Label>{field.label}{field.required && <span className="text-red-500 ml-1">*</span>}</Label>
                    {field.type === "dropdown" ? (
                      <Select value={fillData[field.id || field.name] || ""} onValueChange={(v) => setFillData({ ...fillData, [field.id || field.name]: v })}>
                        <SelectTrigger><SelectValue placeholder={`Select ${field.label}...`} /></SelectTrigger>
                        <SelectContent>{(field.options || []).map(opt => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}</SelectContent>
                      </Select>
                    ) : field.type === "textarea" ? (
                      <Textarea value={fillData[field.id || field.name] || ""} onChange={(e) => setFillData({ ...fillData, [field.id || field.name]: e.target.value })} rows={3} placeholder={field.placeholder || ""} />
                    ) : field.type === "date" ? (
                      <Input type="date" value={fillData[field.id || field.name] || ""} onChange={(e) => setFillData({ ...fillData, [field.id || field.name]: e.target.value })} />
                    ) : field.type === "number" ? (
                      <Input type="number" value={fillData[field.id || field.name] || ""} onChange={(e) => setFillData({ ...fillData, [field.id || field.name]: e.target.value })} placeholder={field.placeholder || ""} />
                    ) : field.type === "repeater" ? (
                      <RepeaterField field={field} value={fillData[field.id || field.name] || []} onChange={(v) => setFillData({ ...fillData, [field.id || field.name]: v })} />
                    ) : (
                      <Input value={fillData[field.id || field.name] || ""} onChange={(e) => setFillData({ ...fillData, [field.id || field.name]: e.target.value })} placeholder={field.placeholder || ""} />
                    )}
                    </>
                    )}
                  </div>
                ))}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowFillModal(false)}>Cancel</Button>
                <Button onClick={async () => {
                  try {
                    await axios.post(`${API}/forms/${selectedForm.form_id}/submit`, fillData);
                    toast.success("Form submitted!");
                    setShowFillModal(false);
                    setFillData({});
                    fetchForms();
                  } catch (err) { toast.error("Failed to submit"); }
                }}>Submit</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Form Modal */}
      <Dialog open={showEditModal} onOpenChange={(v) => { setShowEditModal(v); if (!v) setEditingForm(null); }}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
          {editingForm && (
            <>
              <DialogHeader>
                <DialogTitle>Edit Form: {editingForm.name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 py-2">
                <div>
                  <Label>Form Name</Label>
                  <Input value={editingForm.name} onChange={(e) => setEditingForm({ ...editingForm, name: e.target.value })} />
                </div>
                <Label className="font-medium">Fields</Label>
                {(editingForm.fields || []).map((field, idx) => (
                  <div key={idx} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                    <div className="flex-1 grid grid-cols-3 gap-2">
                      <Input value={field.label} onChange={(e) => { const f = [...editingForm.fields]; f[idx] = { ...f[idx], label: e.target.value }; setEditingForm({ ...editingForm, fields: f }); }} placeholder="Label" className="text-sm" />
                      <Select value={field.type} onValueChange={(v) => { const f = [...editingForm.fields]; f[idx] = { ...f[idx], type: v }; setEditingForm({ ...editingForm, fields: f }); }}>
                        <SelectTrigger className="text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="text">Text</SelectItem>
                          <SelectItem value="number">Number</SelectItem>
                          <SelectItem value="date">Date</SelectItem>
                          <SelectItem value="dropdown">Dropdown</SelectItem>
                          <SelectItem value="textarea">Text Area</SelectItem>
                          <SelectItem value="repeater">Repeater</SelectItem>
                        </SelectContent>
                      </Select>
                      {field.type === "dropdown" && (
                        <Input value={(field.options || []).join(", ")} onChange={(e) => { const f = [...editingForm.fields]; f[idx] = { ...f[idx], options: e.target.value.split(",").map(o => o.trim()).filter(Boolean) }; setEditingForm({ ...editingForm, fields: f }); }} placeholder="Options (comma separated)" className="text-sm" />
                      )}
                    </div>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400 shrink-0" onClick={() => { const f = editingForm.fields.filter((_, i) => i !== idx); setEditingForm({ ...editingForm, fields: f }); }}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={() => setEditingForm({ ...editingForm, fields: [...(editingForm.fields || []), { id: `field_${Date.now()}`, label: "", type: "text", options: [] }] })}>
                  <Plus className="w-3 h-3 mr-1" /> Add Field
                </Button>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowEditModal(false)}>Cancel</Button>
                <Button onClick={async () => {
                  try {
                    await axios.put(`${API}/forms/${editingForm.form_id}`, { name: editingForm.name, fields: editingForm.fields });
                    toast.success("Form updated!");
                    setShowEditModal(false);
                    fetchForms();
                  } catch (err) { toast.error("Failed to update"); }
                }}>Save Changes</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
      <NewSheetDialog open={showScratchpad} onOpenChange={setShowScratchpad} scratchpadOnly />
    </div>
  );
}

function RepeaterField({ field, value, onChange }) {
  const rows = Array.isArray(value) ? value : [];
  const subFields = field.sub_fields || [];
  const addRow = () => onChange([...rows, {}]);
  const updateRow = (idx, key, val) => {
    const updated = [...rows];
    updated[idx] = { ...updated[idx], [key]: val };
    onChange(updated);
  };
  const removeRow = (idx) => onChange(rows.filter((_, i) => i !== idx));

  return (
    <div className="space-y-2">
      {rows.map((row, idx) => (
        <div key={idx} className="flex items-center gap-2 p-2 bg-slate-50 rounded">
          <span className="text-xs text-slate-400 w-5">{idx + 1}.</span>
          {subFields.map(sf => (
            <Input key={sf.id} value={row[sf.id] || ""} onChange={(e) => updateRow(idx, sf.id, e.target.value)} placeholder={sf.label} className="text-sm" />
          ))}
          <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400 shrink-0" onClick={() => removeRow(idx)}>
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={addRow}><Plus className="w-3 h-3 mr-1" /> Add Row</Button>
    </div>
  );
}
